<template>
  <div class="produtos-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="blue-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-package-variant</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Consulta de Produtos</h1>
              <div class="text-caption text-grey">
                Produtos cadastrados com informações de estoque
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
         
          </div>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSÍVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <!-- Seção Esquerda - Título e Info -->
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} produtos
              </v-chip>
              
              <v-chip 
                v-if="hasActiveFilters"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>
          
          <!-- Seção Central - Botões de Ação -->
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="fetchData"
              :loading="loading"
            >
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>
            
            <v-btn
            class="ml-5"
              color="white"
              variant="flat"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <!-- Botão Expandir/Recolher -->
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
            <v-tooltip activator="parent">
              {{ filtersExpanded ? 'Recolher Filtros' : 'Expandir Filtros' }}
            </v-tooltip>
          </v-btn>
        </div>
      </v-card-title>
      
      <!-- Conteúdo Expansível -->
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.codigo_produto"
                  label="Código do Produto"
                  placeholder="Ex: P001..."
                  prepend-inner-icon="mdi-barcode"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.nome_produto"
                  label="Nome do Produto"
                  placeholder="Buscar por nome..."
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="filtros.grupos_produtos"
                  :items="gruposProdutos"
                  item-title="nome_grupo_produto"
                  item-value="codigo_grupo"
                  label="Grupos de Produtos"
                  placeholder="Selecione um ou mais grupos"
                  prepend-inner-icon="mdi-shape-outline"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  multiple
                  chips
                  closable-chips
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 2" size="small" closable @click:close="removeGrupo(index)">
                      {{ item.title }}
                    </v-chip>
                    <span v-if="index === 2" class="text-grey text-caption ml-1">
                      (+{{ filtros.grupos_produtos.length - 2 }} outros)
                    </span>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filtros.id_master"
                  label="ID Master"
                  placeholder="Ex: 0001"
                  prepend-inner-icon="mdi-identifier"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filtros.id_tamanho"
                  label="ID Tamanho"
                  placeholder="Ex: 38"
                  prepend-inner-icon="mdi-ruler"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.estoque_fisico_min"
                  label="Est. Físico ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-package-variant"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.comprometido_min"
                  label="Comprom. ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-lock"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="filtros.disponivel_min"
                  label="Disponível ≥"
                  placeholder="Mín."
                  prepend-inner-icon="mdi-check-circle"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  type="number"
                  @keyup.enter="fetchData"
                />
              </v-col>

              <v-col cols="12" md="4" class="d-flex justify-end align-center">
                <v-btn
                  color="blue-darken-1"
                  variant="flat"
                  @click="fetchData"
                  :loading="loading"
                >
                  <v-icon start>mdi-filter</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="produtos"
        :items-length="pagination.total"
        :loading="loading"
        :page="pagination.current_page"
        :sort-by="sortBy"
        item-key="codigo_produto"
        class="scrollable-table"
        density="compact"
        fixed-header
        height="calc(100vh - 420px)"
        @update:options="onOptionsUpdate"
      >
        <!-- Código -->
        <template #item.codigo_produto="{ item }">
          <v-chip
            color="blue-darken-1"
            variant="tonal"
            size="small"
          >
            {{ item.codigo_produto }}
          </v-chip>
        </template>

        <!-- Nome -->
        <template #item.nome_produto="{ item }">
          <v-chip
            color="teal"
            variant="tonal"
            size="small"
          >
            {{ item.nome_produto }}
          </v-chip>
        </template>

        <!-- Grupo -->
        <template #item.nome_grupo_produto="{ item }">
          <v-chip
            color="purple"
            variant="tonal"
            size="x-small"
          >
            {{ item.nome_grupo_produto || 'Sem grupo' }}
          </v-chip>
        </template>

        <!-- ID Master -->
        <template #item.id_master="{ item }">
          <span class="text-caption">{{ item.id_master }}</span>
        </template>

        <!-- ID Tamanho -->
        <template #item.id_tamanho="{ item }">
          <v-chip
            color="blue-grey"
            variant="tonal"
            size="x-small"
          >
            {{ item.id_tamanho }}
          </v-chip>
        </template>

        <!-- Estoque Físico - Clicável para ver detalhes -->
        <template #item.estoque_fisico="{ item }">
          <v-btn
            variant="text"
            size="small"
            :class="item.estoque_fisico > 0 ? 'text-success font-weight-bold' : 'text-grey'"
            @click="abrirDialogEstoque(item)"
            class="pa-0"
          >
            {{ formatNumber(item.estoque_fisico) }}
            <v-icon size="14" class="ml-1">mdi-magnify</v-icon>
          </v-btn>
        </template>

        <!-- Comprometido -->
        <template #item.comprometido="{ item }">
          <span :class="item.comprometido > 0 ? 'text-warning' : 'text-grey'">
            {{ formatNumber(item.comprometido) }}
          </span>
        </template>

        <!-- Disponível -->
        <template #item.disponivel_previsto="{ item }">
          <span :class="item.disponivel_previsto > 0 ? 'text-success' : 'text-error'">
            {{ formatNumber(item.disponivel_previsto) }}
          </span>
        </template>

        <template #item.saldo_gestor_pendente="{ item }">
          <span :class="saldoGestorItem(item) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold'">
            {{ formatNumberSigned(saldoGestorItem(item)) }}
          </span>
        </template>

        <template #item.saldo_projetado="{ item }">
          <span class="text-primary font-weight-black">
            {{ formatNumber(saldoCalculadoItem(item, 'produto')) }}
          </span>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhum produto encontrado. Ajuste os filtros e clique em "Filtrar".
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- ================= DIALOG ESTOQUE POR DEPÓSITO ================= -->
    <v-dialog v-model="dialogEstoque" max-width="1080" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-warehouse</v-icon>
          Estoque por Depósito
          <v-spacer />
          <span class="text-caption">ref# dg-cp-dep</span>
        </v-card-title>
        <v-card-subtitle class="bg-blue-darken-1 text-white pb-3 text-no-wrap overflow-hidden text-truncate">
          <strong>{{ produtoSelecionado?.codigo_produto }}</strong> - {{ produtoSelecionado?.nome_produto }}
        </v-card-subtitle>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headersEstoqueDeposito"
            :items="estoquesDeposito"
            :loading="loadingEstoque"
            density="compact"
            class="elevation-0"
            :items-per-page="-1"
            hide-default-footer
          >
            <template #item.nome_deposito="{ item }">
              <div class="d-flex align-center deposito-cell">
                <span class="text-truncate">{{ item.nome_deposito || `Deposito ${item.codigo_deposito || '-'}` }}</span>
                <v-chip v-if="isSomenteGestor(item, 'deposito')" color="warning" variant="tonal" size="x-small" class="ml-2">
                  Somente Gestor
                </v-chip>
              </div>
            </template>
            
            <template #item.estoque_fisico="{ item }">
              <v-btn
                variant="text"
                size="small"
                :class="saldoCalculadoItem(item, 'deposito') > 0 ? 'text-success font-weight-bold' : 'text-grey'"
                @click="abrirDialogLocalizacao(item)"
                class="pa-0"
                :disabled="saldoCalculadoItem(item, 'deposito') <= 0"
              >
                {{ formatNumber(item.estoque_fisico) }}
                <v-icon v-if="saldoCalculadoItem(item, 'deposito') > 0" size="14" class="ml-1">mdi-magnify</v-icon>
              </v-btn>
            </template>
            
            <template #item.estoque_comprometido="{ item }">
              <span :class="item.estoque_comprometido > 0 ? 'text-warning' : 'text-grey'">
                {{ formatNumber(item.estoque_comprometido) }}
              </span>
            </template>
            
            <template #item.estoque_disponivel="{ item }">
              <span :class="item.estoque_disponivel > 0 ? 'text-success font-weight-bold' : 'text-error'">
                {{ formatNumber(item.estoque_disponivel) }}
              </span>
            </template>

            <template #item.saldo_gestor_pendente="{ item }">
              <span :class="saldoGestorItem(item) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold'">
                {{ formatNumberSigned(saldoGestorItem(item)) }}
              </span>
            </template>

            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatNumber(saldoCalculadoItem(item, 'deposito')) }}
              </span>
            </template>
            
            <template #no-data>
              <div class="text-center pa-4 text-grey">
                Nenhum estoque encontrado para este produto.
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        
        <v-divider />
        
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogEstoque = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG ESTOQUE POR LOCALIZAÇÃO (BIN) ================= -->
    <v-dialog v-model="dialogLocalizacao" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-teal-darken-1 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-map-marker-outline</v-icon>
          Saldos por Localização
          <v-spacer />
          <span class="text-caption">ref# dg-cp-loc</span>
        </v-card-title>
        <v-card-subtitle class="bg-teal-darken-1 text-white pb-3">
          <strong>{{ depositoSelecionado?.nome_deposito }}</strong> - {{ produtoSelecionado?.nome_produto }}
        </v-card-subtitle>
        
        <v-card-text class="pa-0">
          <v-data-table
            :headers="headersEstoqueLocalizacao"
            :items="estoquesLocalizacao"
            :loading="loadingLocalizacao"
            density="compact"
            class="elevation-0"
            :items-per-page="-1"
            hide-default-footer
          >
            <!-- Customização de Rua/Prateleira/Nivel/Posição -->
            <template #item.codigo_localizacao="{ item }">
              <span class="font-weight-medium">
                {{ item.codigo_localizacao || 'SEM LOCALIZACAO' }}
              </span>
            </template>
            <template #item.rua="{ item }">
              <v-chip color="secondary" variant="tonal" size="x-small" label>
                {{ item.rua || '-' }}
              </v-chip>
            </template>
            <template #item.prateleira="{ item }">
              <v-chip color="info" variant="tonal" size="x-small" label>
                {{ item.prateleira || '-' }}
              </v-chip>
            </template>
            <template #item.nivel="{ item }">
              <v-chip color="primary" variant="tonal" size="x-small" label>
                {{ item.nivel || '-' }}
              </v-chip>
            </template>
            <template #item.posicao="{ item }">
              <v-chip color="brown" variant="tonal" size="x-small" label>
                {{ item.posicao || '-' }}
              </v-chip>
            </template>

            <template #item.estoque_localizacao="{ item }">
              <span class="text-success font-weight-bold">
                {{ formatNumber(item.estoque_localizacao) }}
              </span>
            </template>

            <template #item.saldo_gestor_pendente="{ item }">
              <span :class="saldoGestorItem(item) >= 0 ? 'text-success font-weight-bold' : 'text-error font-weight-bold'">
                {{ formatNumberSigned(saldoGestorItem(item)) }}
              </span>
            </template>

            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatNumber(saldoCalculadoItem(item, 'localizacao')) }}
              </span>
            </template>
            
            <template #no-data>
              <div class="text-center pa-4 text-grey">
                Nenhuma localização encontrada para este produto neste depósito.
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        
        <v-divider />
        
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogLocalizacao = false">
            Voltar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { sapeJodarcService } from "@/services/sapeJodarcService";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "ConsultaProdutos",

  data() {
    return {
      produtos: [],
      gruposProdutos: [],
      loading: false,
      initialized: false,
      itemsPerPage: 50,
      filtersExpanded: false,
      sortBy: [],

      // Dialog estoque por depósito
      dialogEstoque: false,
      loadingEstoque: false,
      produtoSelecionado: null,
      estoquesDeposito: [],

      // Dialog estoque por localização
      dialogLocalizacao: false,
      loadingLocalizacao: false,
      depositoSelecionado: null,
      estoquesLocalizacao: [],

      filtros: {
        codigo_produto: "",
        nome_produto: "",
        grupos_produtos: [],
        id_master: "",
        id_tamanho: "",
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      },

      pagination: {
        total: 0,
        per_page: 50,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
      },

      headers: [
        { title: "Código", key: "codigo_produto", width: "130px" },
        { title: "Produto", key: "nome_produto" },
        { title: "Grupo", key: "nome_grupo_produto", width: "150px" },
        { title: "Master", key: "id_master", width: "80px" },
        { title: "Tam.", key: "id_tamanho", width: "70px" },
        { title: "Est. Físico", key: "estoque_fisico", width: "120px", align: "end" },
        { title: "Comprom.", key: "comprometido", width: "100px", align: "end" },
        { title: "Disponível", key: "disponivel_previsto", width: "100px", align: "end" },
        { title: "Gestor", key: "saldo_gestor_pendente", width: "100px", align: "end" },
        { title: "Calculado", key: "saldo_projetado", width: "110px", align: "end" },
      ],

      headersEstoqueDeposito: [
        { title: "Deposito", key: "nome_deposito", width: "320px" },
        { title: "SAP-FISICO", key: "estoque_fisico", width: "120px", align: "end" },
        { title: "Comprometido", key: "estoque_comprometido", width: "120px", align: "end" },
        { title: "Disponivel", key: "estoque_disponivel", width: "120px", align: "end" },
        { title: "Gestor", key: "saldo_gestor_pendente", width: "100px", align: "end" },
        { title: "Calculado", key: "saldo_projetado", width: "110px", align: "end" },
      ],

      headersEstoqueLocalizacao: [
        { title: "Localização", key: "codigo_localizacao" },
        { title: "Rua", key: "rua" },
        { title: "Prat.", key: "prateleira" },
        { title: "Nível", key: "nivel" },
        { title: "Pos.", key: "posicao" },
        { title: "Estoque", key: "estoque_localizacao", align: "end" },
        { title: "Gestor", key: "saldo_gestor_pendente", align: "end" },
        { title: "Calculado", key: "saldo_projetado", align: "end" },
      ],
    };
  },

  computed: {
    hasActiveFilters() {
      return (
        this.filtros.codigo_produto ||
        this.filtros.nome_produto ||
        this.filtros.grupos_produtos.length > 0 ||
        this.filtros.id_master ||
        this.filtros.id_tamanho ||
        this.filtros.estoque_fisico_min !== null ||
        this.filtros.comprometido_min !== null ||
        this.filtros.disponivel_min !== null
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filtros.codigo_produto) count++;
      if (this.filtros.nome_produto) count++;
      if (this.filtros.grupos_produtos.length > 0) count++;
      if (this.filtros.id_master) count++;
      if (this.filtros.id_tamanho) count++;
      if (this.filtros.estoque_fisico_min !== null) count++;
      if (this.filtros.comprometido_min !== null) count++;
      if (this.filtros.disponivel_min !== null) count++;
      return count;
    },
  },

  methods: {
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          ...this.montarFiltrosProdutoFlexiveis(),
          grupos_produtos: this.filtros.grupos_produtos.length > 0 ? this.filtros.grupos_produtos : undefined,
          id_master: this.filtros.id_master || undefined,
          id_tamanho: this.filtros.id_tamanho || undefined,
          estoque_fisico_min: this.filtros.estoque_fisico_min ?? undefined,
          comprometido_min: this.filtros.comprometido_min ?? undefined,
          disponivel_min: this.filtros.disponivel_min ?? undefined,
          sort_by: this.sortBy.length > 0 ? this.sortBy[0].key : undefined,
          sort_order: this.sortBy.length > 0 ? this.sortBy[0].order : undefined,
        };

        console.log('🔍 Ordenação:', { sortBy: this.sortBy, params_sort_by: params.sort_by, params_sort_order: params.sort_order });

        const response = await sapeJodarcService.sapeProdutos(params);

        if (response?.success) {
          this.produtos = Array.isArray(response.data) ? response.data : [];
          if (response.pagination) {
            this.pagination = response.pagination;
            this.inputPage = response.pagination.current_page;
          }
        } else {
          this.produtos = [];
          toast.error(response?.message || "Erro ao carregar produtos");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar produtos");
        this.produtos = [];
      } finally {
        this.loading = false;
      }
    },

    montarFiltrosProdutoFlexiveis() {
      const codigo = String(this.filtros.codigo_produto || '').trim();
      const nome = String(this.filtros.nome_produto || '').trim();
      const filtros = {};

      if (nome) {
        filtros.nome_produto = nome;
      }

      if (codigo) {
        const pareceDescricao = /[%_\s]/.test(codigo) || !/\d/.test(codigo);
        if (pareceDescricao && !nome) {
          filtros.nome_produto = codigo;
        } else {
          filtros.codigo_produto = codigo;
        }
      }

      return filtros;
    },

    async fetchGrupos() {
      try {
        const response = await sapeJodarcService.sapeProdutosGrupo({});
        if (response?.success) {
          this.gruposProdutos = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar grupos:", error);
      }
    },

    onOptionsUpdate({ page, itemsPerPage, sortBy }) {
      if (this.loading) return;
      
      const currentSortBy = JSON.stringify(this.sortBy);
      const newSortBy = JSON.stringify(sortBy || []);
      
      if (!this.initialized) {
        this.initialized = true;
        this.pagination.current_page = page;
        this.pagination.per_page = itemsPerPage;
        this.itemsPerPage = itemsPerPage;
        this.sortBy = sortBy || [];
        this.fetchData();
        return;
      }
      
      const pageChanged = page !== this.pagination.current_page;
      const perPageChanged = itemsPerPage !== this.pagination.per_page;
      const sortChanged = currentSortBy !== newSortBy;
      
      if (!pageChanged && !perPageChanged && !sortChanged) return;
      
      this.pagination.current_page = page;
      this.itemsPerPage = itemsPerPage;
      this.pagination.per_page = itemsPerPage;
      this.sortBy = sortBy || [];
      this.fetchData();
    },

    limparFiltros() {
      this.filtros = {
        codigo_produto: "",
        nome_produto: "",
        grupos_produtos: [],
        id_master: "",
        id_tamanho: "",
        estoque_fisico_min: null,
        comprometido_min: null,
        disponivel_min: null,
      };
      this.pagination.current_page = 1;
      this.fetchData();
    },

    removeGrupo(index) {
      this.filtros.grupos_produtos.splice(index, 1);
    },

    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    formatNumberSigned(value) {
      const number = Number(value || 0);
      const formatted = Math.abs(number).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      return number > 0 ? `+${formatted}` : number < 0 ? `-${formatted}` : "0";
    },

    isSameDeposito(id1, id2) {
      if (id1 === null || id1 === undefined || id2 === null || id2 === undefined) return false;
      const s1 = String(id1).trim();
      const s2 = String(id2).trim();
      return s1 === s2 || s1.padStart(2, '0') === s2.padStart(2, '0') || Number(s1) === Number(s2);
    },

    saldoGestorItem(item) {
      return Number(item?.entradas_pendentes || 0) - Number(item?.saidas_pendentes || 0);
    },

    saldoCalculadoItem(item, nivel = 'produto') {
      if (item?.saldo_projetado !== undefined && item?.saldo_projetado !== null) {
        return Number(item.saldo_projetado || 0);
      }

      const base = nivel === 'localizacao'
        ? Number(item?.estoque_localizacao || 0)
        : Number(item?.estoque_fisico || 0);

      return base + this.saldoGestorItem(item);
    },

    isSomenteGestor(item, nivel = 'deposito') {
      const sap = nivel === 'localizacao'
        ? Number(item?.estoque_localizacao || 0)
        : Number(item?.estoque_fisico || 0);
      return sap <= 0 && this.saldoGestorItem(item) !== 0;
    },

    textoLocalizacao(valor) {
      const texto = String(valor || '').trim().replace(/\s+/g, ' ');
      if (!texto || texto === '-' || texto.toUpperCase() === 'SEM LOCALIZACAO') {
        return 'SEM LOCALIZACAO';
      }
      return texto;
    },

    textoEndereco(valor) {
      const texto = String(valor || '').trim().replace(/\s+/g, ' ');
      if (!texto || texto === '-' || texto.toUpperCase() === 'SEM LOCALIZACAO') {
        return '';
      }
      return texto;
    },

    isSemLocalizacao(item) {
      const codigo = this.textoLocalizacao(item?.codigo_localizacao || item?.localizacao || item?.posicao);
      const rua = this.textoEndereco(item?.rua);
      const prateleira = this.textoEndereco(item?.prateleira);
      const nivel = this.textoEndereco(item?.nivel);
      const posicao = this.textoLocalizacao(item?.posicao);
      return codigo === 'SEM LOCALIZACAO' && !rua && !prateleira && !nivel && posicao === 'SEM LOCALIZACAO';
    },

    agruparEstoquesLocalizacao(itens) {
      const mapa = {};

      (itens || []).forEach(item => {
        const semLocalizacao = this.isSemLocalizacao(item);
        const codigo = semLocalizacao
          ? 'SEM LOCALIZACAO'
          : this.textoLocalizacao(item.codigo_localizacao || item.localizacao || item.posicao);
        const rua = semLocalizacao ? '' : this.textoEndereco(item.rua);
        const prateleira = semLocalizacao ? '' : this.textoEndereco(item.prateleira);
        const nivel = semLocalizacao ? '' : this.textoEndereco(item.nivel);
        const posicao = semLocalizacao ? 'SEM LOCALIZACAO' : (item.posicao || codigo);
        const key = semLocalizacao
          ? 'SEM_LOCALIZACAO'
          : [codigo, rua, prateleira, nivel, posicao].map(v => String(v || '').trim()).join('|');

        if (!mapa[key]) {
          mapa[key] = {
            ...item,
            codigo_localizacao: codigo,
            localizacao: codigo,
            rua,
            prateleira,
            nivel,
            posicao,
            estoque_localizacao: 0,
            entradas_pendentes: 0,
            saidas_pendentes: 0,
            saldo_projetado: 0,
          };
        }

        mapa[key].estoque_localizacao += Number(item.estoque_localizacao || 0);
        mapa[key].entradas_pendentes += Number(item.entradas_pendentes || 0);
        mapa[key].saidas_pendentes += Number(item.saidas_pendentes || 0);
        mapa[key].saldo_projetado += Number(item.saldo_projetado ?? this.saldoCalculadoItem(item, 'localizacao'));
      });

      return Object.values(mapa).sort((a, b) => {
        if (a.codigo_localizacao === 'SEM LOCALIZACAO') return 1;
        if (b.codigo_localizacao === 'SEM LOCALIZACAO') return -1;
        return String(a.codigo_localizacao || '').localeCompare(String(b.codigo_localizacao || ''));
      });
    },

    voltarMenu() {
      this.$router.push({ name: "Menu" });
    },

    async abrirDialogEstoque(produto) {
      this.produtoSelecionado = produto;
      this.dialogEstoque = true;
      this.loadingEstoque = true;
      this.estoquesDeposito = [];

      try {
        const params = {
          codigo_produto: produto.codigo_produto,
          per_page: 100,
        };
        const response = await sapeJodarcService.sapeProdutosEstoquesDepositos(params);

        if (response?.success) {
          this.estoquesDeposito = Array.isArray(response.data) ? response.data : [];
        } else {
          toast.error(response?.message || "Erro ao carregar estoques");
        }
      } catch (error) {
        console.error("Erro ao carregar estoques:", error);
        toast.error("Erro ao carregar estoques por depósito");
      } finally {
        this.loadingEstoque = false;
      }
    },

    async abrirDialogLocalizacao(deposito) {
      if (this.saldoCalculadoItem(deposito, 'deposito') <= 0) return;
      
      this.depositoSelecionado = deposito;
      this.dialogLocalizacao = true;
      this.loadingLocalizacao = true;
      this.estoquesLocalizacao = [];

      try {
        const params = {
          codigo_produto: this.produtoSelecionado.codigo_produto,
          codigo_deposito: deposito.codigo_deposito,
          per_page: 100,
        };
        const response = await sapeJodarcService.sapeProdutosEstoquesLocalizacoes(params);

        if (response?.success) {
          const localizacoes = Array.isArray(response.data?.data)
            ? response.data.data
            : (Array.isArray(response.data) ? response.data : []);
          const localizacoesDoDeposito = localizacoes.filter(item =>
            this.isSameDeposito(item.codigo_deposito || item.deposito || item.id_deposito, deposito.codigo_deposito)
          );
          this.estoquesLocalizacao = this.agruparEstoquesLocalizacao(localizacoesDoDeposito);
        } else {
          toast.error(response?.message || "Erro ao carregar localizações");
        }
      } catch (error) {
        console.error("Erro ao carregar localizações:", error);
        toast.error("Erro ao carregar estoques por localização");
      } finally {
        this.loadingLocalizacao = false;
      }
    },
  },

  mounted() {
    this.fetchGrupos();
  },
};
</script>

<style scoped>
/* ✅ CONTAINER PRINCIPAL */
.produtos-container {
  padding: 24px;
  background: #e4edfb;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 16px;
}

/* ✅ ESTILOS DOS FILTROS */
.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.deposito-cell {
  min-width: 0;
  max-width: 320px;
}

.filters-header {
  background: linear-gradient(135deg, #1976d2, #1565c0);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 600;
}

/* ✅ ROTAÇÃO DO ÍCONE EXPANDIR */
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

/* ✅ ESTILOS DA TABELA */
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

/* ✅ SCROLL CUSTOMIZADO PARA TABELA */
.scrollable-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
  overflow-y: auto !important;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 4px;
}

.scrollable-table :deep(.v-table__wrapper)::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

/* ✅ HEADERS DA TABELA */
.scrollable-table :deep(.v-data-table__th) {
  font-weight: 600 !important;
  background-color: #f8f9fa !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 10 !important;
}

/* ✅ COLUNA CÓDIGO FIXA */
.scrollable-table :deep(.v-data-table__th:first-child),
.scrollable-table :deep(.v-data-table__td:first-child) {
  position: sticky !important;
  left: 0 !important;
  z-index: 5 !important;
  background: white !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1) !important;
}

.scrollable-table :deep(.v-data-table__th:first-child) {
  z-index: 15 !important;
  background: #f8f9fa !important;
}

/* ✅ HOVER DA LINHA */
.scrollable-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.scrollable-table :deep(.v-data-table__tr:hover .v-data-table__td:first-child) {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

/* ✅ TRUNCATE */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ✅ RESPONSIVIDADE */
@media (max-width: 768px) {
  .produtos-container {
    padding: 16px;
  }
  
  .scrollable-table :deep(.v-data-table__th),
  .scrollable-table :deep(.v-data-table__td) {
    font-size: 0.875rem;
  }
}
</style>
